'use client'

import { Download } from '@carbon/icons-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { AnimatedNumber } from '@/components/ui/animated-number'
import {
  EASE,
  Eyebrow,
  HAIRLINE,
  PANEL,
  SectionHead,
  useReveal,
} from '@/components/page-kit'

/* The brochure page's structural device is the document itself: a dossier set
   in numbered folios. The hero is the cover, a ruled contents strip indexes
   every section, each section carries its printed number, and the feature
   lists are set as specification sheets — ruled lines with group headings —
   rather than marketing pills. Closes, like the bespoke product pages, on a
   circuit field and the download.

   Every user-facing string on this page is carried verbatim from the previous
   version — layout is redesigned, copy is not. */

const PDF_HREF = '/IntelQI Product Brochure.pdf'

// ─── Source copy (verbatim) ──────────────────────────────────────────────────

const IMAGES = {
  about: '/stories/substories/iima.webp',
  campus: '/stories/campus.webp',
  team: '/images/working_team_brochure.avif',
  data: '/images/student_on_computer_brochure.webp',
  exam: '/images/student_exam_brochure.webp',
  finance:
    'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&q=80',
  implementation: '/images/implimentation_brochure.webp',
  faculty: '/images/faculty.webp',
}

const MODULES = [
  {
    num: '01',
    name: 'Student Information Management System',
    cat: 'Academic Data Management',
  },
  { num: '02', name: 'Campus Administration', cat: 'Institutional Operations' },
  {
    num: '03',
    name: 'Faculty Management System',
    cat: 'Human Resource Management',
  },
  { num: '04', name: 'Examination & Evaluation', cat: 'Academic Assessment' },
  { num: '05', name: 'Finance & Accounts', cat: 'Financial Management' },
]

const IMPLEMENTATION_STEPS = [
  {
    num: '01',
    title: 'Discovery & Planning',
    desc: 'Identify operational gaps and strategize areas for intervention to align with campus goals.',
  },
  {
    num: '02',
    title: 'System Configuration',
    desc: 'Implement specific policies through configuration and deploy the customized ERP for daily use.',
  },
  {
    num: '03',
    title: 'Database Consolidation',
    desc: 'Transfer existing records using the ETL process to ensure accurate and centralized data storage.',
  },
  {
    num: '04',
    title: 'Deployment & Training',
    desc: "Configure the institution's specific policies and rules, followed by comprehensive user training.",
  },
  {
    num: '05',
    title: 'Support & Optimization',
    desc: 'Continuous support and upgrades, monitoring activities to ensure optimal post-live performance.',
  },
]

type FeatureGroup = { label: string; items: string[]; accent?: boolean }
type ModuleColumn = { header: string; groups: FeatureGroup[] }

type ModuleDef = {
  id: string
  num: string
  label: string
  title: string
  titleAccent: string
  desc: string
  img: string
  imgLabel: string
  flip?: boolean
  columns: ModuleColumn[]
}

const MODULE_SECTIONS: ModuleDef[] = [
  {
    id: 'module-01',
    num: 'Module 01',
    label: 'Academic Data Management',
    title: 'Student Information',
    titleAccent: 'Management System',
    desc: 'A unified engine for managing leads, admissions, and fees supporting complex multi-campus workflows and multi-currency structures with seamless scalability.',
    img: IMAGES.data,
    imgLabel: 'Student Data Intelligence',
    columns: [
      {
        header: 'Onboarding & Identity',
        groups: [
          {
            accent: true,
            label: 'Smart Identity',
            items: [
              'Automate Student ID Generation',
              'Issue Smart / RFID Identity Cards',
              'Student Certificate Generation',
            ],
          },
          {
            label: 'Mentorship',
            items: [
              'Automated Mentor Allotment',
              'Orientation Management',
              'Roll Lists & Student Services',
              'Student MIS Reports',
            ],
          },
        ],
      },
      {
        header: 'Admission & Config',
        groups: [
          {
            accent: true,
            label: 'Organization Setup',
            items: [
              'Define Institute, Degree, Programs',
              'Configure Fees & Course Parameters',
              'Global Fee Configurations',
            ],
          },
          {
            label: 'Application Process',
            items: [
              'Application Management',
              'Direct Admission Management',
              'Merit List Generation',
              'Aptitude Testing',
              'Offer Letter Issuance',
            ],
          },
        ],
      },
      {
        header: 'Fees & Reporting',
        groups: [
          {
            accent: true,
            label: 'Fee Management',
            items: [
              'Fees Collection (Multi-currency)',
              'Scholarship Management',
              'Installments & Concessions',
              'Program Transfer Management',
            ],
          },
          {
            label: 'Analytics',
            items: [
              'Daily / Monthly Fee Reports',
              'Outstanding Fee & Bank Reports',
              'Fees Data Export to Excel',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'module-02',
    num: 'Module 02',
    label: 'Institutional Operations',
    title: 'Campus',
    titleAccent: 'Administration',
    desc: 'Achieve institutional goals through centralized monitoring and data-driven strategies. Complete control over facility operations, compliance, and student welfare systems.',
    img: IMAGES.campus,
    imgLabel: 'Centralized Campus Operations',
    flip: true,
    columns: [
      {
        header: 'Training & Placement',
        groups: [
          {
            accent: true,
            label: 'Placement Workflow',
            items: [
              'Multi-Role Logins',
              'Registration Management',
              'Selection Records',
              'Hiring Closure',
            ],
          },
          {
            label: 'Placement Analytics',
            items: ['Analytics & Reports', 'Training Management'],
          },
          {
            label: 'Hostel Management',
            items: [
              'Hostel Structuring & Admissions',
              'Room & Mess Management',
              'Mess Billing',
              'Attendance Management',
              'Dues Recovery',
            ],
          },
        ],
      },
      {
        header: 'Operations & Compliance',
        groups: [
          {
            accent: true,
            label: 'Campus Operations',
            items: [
              'Alumni Management',
              'Visitor Management',
              'Transport Management',
              'Accreditation Management',
            ],
          },
          {
            label: 'Accreditation Data',
            items: [
              'Access Control',
              'Evaluation Dashboards',
              'Record Management',
              'Statistical Analysis',
            ],
          },
          {
            label: 'Data & Access',
            items: [
              'Role-Based Access Control',
              'Bulk Records Management',
              'Data Export',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'module-03',
    num: 'Module 03',
    label: 'Human Resource Management',
    title: 'Faculty Management',
    titleAccent: 'System',
    desc: 'A complete HR ecosystem for academic institutions managing the full faculty lifecycle from recruitment to payroll and statutory compliance.',
    img: IMAGES.faculty,
    imgLabel: 'Faculty Lifecycle Management',
    columns: [
      {
        header: 'Attendance & Records',
        groups: [
          {
            accent: true,
            label: 'Leave Automation',
            items: [
              'Policy & Accruals (Standard & Custom)',
              'Opening Balances & Periodic Crediting',
              'Digital Application & Approval Workflow',
              'Biometric & Shift Sync',
              'On-Duty (OD) Applications',
              'Compensatory Off Tracking',
            ],
          },
          {
            label: 'Centralized Records',
            items: [
              'Comprehensive Service Book',
              'Personal, Family, Professional History',
              'Pay, Disciplinary & Leave History',
              'Digital Document Repository',
              'LTC, CPDA & Education Allowance Claims',
            ],
          },
        ],
      },
      {
        header: 'Payroll & Compliance',
        groups: [
          {
            accent: true,
            label: 'Payroll',
            items: [
              'Monthly Payslips & Salary Registers',
              'Bank / Installment Statements',
              'Annual Summaries',
              'Supplementary Bills',
            ],
          },
          {
            label: 'Tax Management',
            items: [
              'Income Tax Rules & Calculation',
              'Form 16 & 24Q Generation',
              '5th / 6th / 7th Pay Commission',
              'DA Arrears Management',
            ],
          },
          {
            label: 'Statutory',
            items: [
              'PF (Provident Fund) Management',
              'ESIC & LIC Reports',
              'PT Abstracts',
              'Income Tax Filings',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'module-04',
    num: 'Module 04',
    label: 'Academic Assessment',
    title: 'Examination &',
    titleAccent: 'Evaluation',
    desc: 'End-to-end examination management from scheduling and hall tickets to result publishing, certification, and analytics dashboards.',
    img: IMAGES.exam,
    imgLabel: 'Digital Examination Ecosystem',
    flip: true,
    columns: [
      {
        header: 'Pre-Examination',
        groups: [
          {
            accent: true,
            label: 'Setup & Scheduling',
            items: [
              'Roll No. & Hall Ticket Allotment',
              'Student Detention Management',
              'Scheduling & Time Table',
              'Paper Audit & Utilization',
              'Exam Registration & Approval',
            ],
          },
          {
            label: 'Digital Exams',
            items: [
              'Online Proctored Examination',
              'Question Randomization',
              'Digital Monitoring',
              'Mobile & Web-based Exams',
              'Malpractice Monitoring',
              'Mock Tests & War Room',
            ],
          },
        ],
      },
      {
        header: 'Evaluation & Certification',
        groups: [
          {
            accent: true,
            label: 'Evaluation',
            items: [
              'Absolute / Relative Grading',
              'Marks & Grade Entry',
              'SGPA, CGPA & YGPA Calculation',
              'Result Publishing & Grievances',
            ],
          },
          {
            label: 'Certification',
            items: [
              'Grade Cards & Mark Sheets',
              'Tabulation Register & Gazette',
              'Provisional Degree Certificates',
              'QR-Code / RFID Certificates',
              'Convocation Reports',
              'Exam Analytics Dashboards',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'module-05',
    num: 'Module 05',
    label: 'Financial Management',
    title: 'Finance &',
    titleAccent: 'Accounts',
    desc: 'A comprehensive financial engine integrating Student Fees and Payroll for automated data transfer ensuring accurate reconciliation, taxation, and final account generation.',
    img: IMAGES.finance,
    imgLabel: 'Integrated Financial Engine',
    columns: [
      {
        header: 'Core Operations',
        groups: [
          {
            accent: true,
            label: 'Core Transactions',
            items: [
              'Receipts, Payments, Contra',
              'Journal Vouchers (JVs)',
              'Automated Ledgers & Day Books',
            ],
          },
          {
            label: 'Banking',
            items: [
              'Bank Reconciliation',
              'Online Payments',
              'Cheque Printing',
            ],
          },
          {
            label: 'Budgeting & Assets',
            items: [
              'Budget Allocation & Monitoring',
              'Fixed Deposits (FD) & Assets',
              'Project Fund Management',
            ],
          },
        ],
      },
      {
        header: 'Advanced Controls',
        groups: [
          {
            accent: true,
            label: 'Reporting',
            items: [
              'Balance Sheet & Trial Balance',
              'Income Statements',
              'Real-time Reports',
            ],
          },
          {
            label: 'Bill Passing',
            items: [
              'Bill Raising, Approvals & Requests',
              'Maker & Checker Approvals',
            ],
          },
          {
            label: 'Integration',
            items: [
              'Student Fees Sync',
              'Payroll Sync',
              'Multi-entity Consolidated Balance Sheet',
            ],
          },
        ],
      },
    ],
  },
]

const LIFECYCLES = [
  { label: 'Student Lifecycle', sub: 'Enrolment to alumni engagement' },
  { label: 'Faculty Lifecycle', sub: 'Recruitment to retirement' },
  {
    label: 'Institute Administration',
    sub: 'Governance, compliance and operations',
  },
]

const ROLE_CARDS = [
  {
    title: 'Management',
    desc: 'BI-driven strategic dashboards and institutional reporting.',
  },
  {
    title: 'Director / Registrar',
    desc: 'Institute-level insights, approvals, and operational oversight.',
  },
  {
    title: 'Department Heads',
    desc: 'Departmental analytics, module access, and transaction approvals.',
  },
  {
    title: 'Staff & Faculty',
    desc: 'Daily academic operations, teaching workflows, and module transactions.',
  },
  {
    title: 'Students & Alumni',
    desc: 'Personal academic records, services, and lifecycle interactions.',
  },
]

const CONTENTS = [
  { num: '01', title: 'About Us', href: '#about', cat: 'Company' },
  {
    num: '02',
    title: 'Platform Overview',
    href: '#overview',
    cat: 'IntelQI UDC',
  },
  ...MODULE_SECTIONS.map((m) => ({
    num: m.num.replace('Module ', 'M·'),
    title: `${m.title} ${m.titleAccent}`,
    href: `#${m.id}`,
    cat: m.label,
  })),
  { num: '06', title: 'Access Hierarchy', href: '#erp', cat: 'ERP Lifecycle' },
  {
    num: '07',
    title: 'A Proven 5-Step Process',
    href: '#implementation',
    cat: 'Implementation',
  },
]

// ─── Shared pieces ───────────────────────────────────────────────────────────

/** The brand accent pair used across the site. */
const ACCENT = 'text-emerald-600 dark:text-primary-active'

function DownloadButton({ large = false }: { large?: boolean }) {
  return (
    <Button asChild size={large ? 'xl' : 'default'} className="gap-2.5">
      <a href={PDF_HREF} download>
        <Download aria-hidden className="size-4 shrink-0" />
        Download full PDF Brochure
      </a>
    </Button>
  )
}

/**
 * Figure in the dossier's language: hairline frame, the photograph fading into
 * the page canvas at its foot, and a plate label set on the rule — the same
 * caption grammar in both themes because the scrim comes from the background
 * token.
 */
function Figure({
  src,
  alt,
  label,
  className,
}: {
  src: string
  alt: string
  label: string
  className?: string
}) {
  const fig = useReveal<HTMLElement>(0.1)

  return (
    <figure
      ref={fig.ref}
      className={cn(
        'relative overflow-hidden rounded-lg border',
        HAIRLINE,
        className
      )}
      style={{
        opacity: fig.shown ? 1 : 0,
        transform: fig.shown ? 'none' : 'translateY(16px)',
        transition: `opacity 700ms ${EASE}, transform 700ms ${EASE}`,
      }}
    >
      {/* Plain img: two sources are remote (unsplash) and the rest are large
          local exports already sized for this page. */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover saturate-[0.8]"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-background via-background/45 to-transparent"
      />
      <figcaption className="absolute bottom-4 left-5">
        <span
          aria-hidden
          className="mb-2 block h-px w-6 bg-emerald-600/70 dark:bg-primary-active/60"
        />
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700 dark:text-primary-active">
          {label}
        </span>
      </figcaption>
    </figure>
  )
}

/** A feature group set as a specification block, not a pill cloud. */
function SpecGroup({ group }: { group: FeatureGroup }) {
  return (
    <div className={cn('border-b py-4 last:border-b-0', 'border-border/40')}>
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em]">
        {group.accent && (
          <span
            aria-hidden
            className="h-3 w-[3px] shrink-0 rounded-full bg-emerald-600 dark:bg-primary-active"
          />
        )}
        <span className={group.accent ? ACCENT : 'text-muted-foreground'}>
          {group.label}
        </span>
      </p>
      <ul className="mt-2.5 space-y-1.5">
        {group.items.map((item) => (
          <li
            key={item}
            className="flex items-baseline gap-2.5 text-sm leading-relaxed text-muted-foreground"
          >
            <span
              aria-hidden
              className="size-1 shrink-0 translate-y-[-2px] rounded-full bg-emerald-600/50 dark:bg-primary-active/40"
            />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Sections ────────────────────────────────────────────────────────────────

function Hero() {
  const intro = useReveal<HTMLDivElement>(0.1)

  return (
    <section
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      {/* Circuit backdrop in the site's shared field, dissolving before the
          contents strip. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
          maskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
        }}
      />

      <div className="container relative z-10 mx-auto">
        <div
          ref={intro.ref}
          className="flex min-h-[86svh] flex-col justify-center pb-16 pt-[calc(var(--header-h)+4.5rem)] sm:pb-20"
          style={{
            opacity: intro.shown ? 1 : 0,
            transform: intro.shown ? 'none' : 'translateY(24px)',
            transition: `opacity 900ms ${EASE}, transform 900ms ${EASE}`,
          }}
        >
          <Eyebrow>Product Brochure · 2026</Eyebrow>

          {/* The wordmark stands in for the display type that used to be set
              here. Sized by WIDTH, not height: the lockup is roughly 7.5:1, so
              a height that reads well on desktop overflows a phone viewport
              several times over. */}
          <h1 className="mt-8">
            <img
              src="/logos/intelqi-logo.svg"
              alt="IntelQI Studios"
              className="h-auto w-full max-w-[19rem] sm:max-w-[26rem] md:max-w-[34rem] lg:max-w-[40rem]"
              /* Above the fold and the largest element in the hero — this is
                 the LCP candidate, so it must not be lazy. */
              fetchPriority="high"
            />
          </h1>

          <p className="mt-7 flex items-center gap-3.5">
            <span
              aria-hidden
              className="h-px w-9 bg-emerald-600 dark:bg-primary-active"
            />
            {/* Links out to the product's own site — same destination and
                external-link semantics as the colophon link at the foot of
                this page, kept in the eyebrow's type style. */}
            <a
              href="https://www.intelqi.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground transition-colors hover:text-foreground"
            >
              www.intelqi.com
            </a>
          </p>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#2B3A46] sm:text-lg dark:text-[#DEE9EE]/90">
            The enterprise-grade platform unifying data, workflows, and
            decision-making as the central operational backbone for institutions
            managing complex, multi-layered systems.
          </p>

          <div className="mt-10 flex w-full flex-col gap-5 sm:w-auto sm:flex-row sm:items-center sm:gap-8">
            <DownloadButton large />

            <p className="flex items-center gap-2.5">
              {['Strategize', 'Implement', 'Achieve'].map((step, i) => (
                <span key={step} className="flex items-center gap-2.5">
                  <span
                    className={cn(
                      'text-[10px] font-semibold uppercase tracking-[0.18em]',
                      i === 1 ? ACCENT : 'text-muted-foreground/70'
                    )}
                  >
                    {step}
                  </span>
                  {i < 2 && (
                    <span
                      aria-hidden
                      className="text-xs text-emerald-600/50 dark:text-primary-active/40"
                    >
                      ›
                    </span>
                  )}
                </span>
              ))}
            </p>
          </div>
        </div>
      </div>

      {/* Folio line — the document's running footer, opened on the cover. */}
      <div className={cn('relative z-10 border-t', HAIRLINE)}>
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-2 py-3.5">
          <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Euclideum Solutions Private Limited
          </span>
          <a
            href="https://www.intelqi.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-medium tracking-[0.1em] text-muted-foreground transition-colors hover:text-foreground"
          >
            www.intelqi.com
          </a>
        </div>
      </div>
    </section>
  )
}

/** The dossier's index: every numbered section as a ruled row. */
function Contents() {
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <section className="pt-14 sm:pt-20 md:pt-24">
      <div className="container mx-auto">
        <Eyebrow>Contents</Eyebrow>

        <div ref={list.ref} className={cn('mt-6 border-t', HAIRLINE)}>
          {CONTENTS.map((entry, i) => (
            <a
              key={entry.href + entry.num}
              href={entry.href}
              className={cn(
                'group grid grid-cols-[2.5rem_1fr] items-baseline gap-x-3 border-b py-4 sm:grid-cols-[3.5rem_1fr_auto] sm:gap-x-6 sm:py-[18px]',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(10px)',
                transition: `opacity 500ms ${EASE} ${i * 60}ms, transform 500ms ${EASE} ${i * 60}ms`,
              }}
            >
              <span className="text-[11px] tabular-nums text-muted-foreground/60 sm:text-xs">
                {entry.num}
              </span>

              <span className="font-heading text-lg font-normal leading-snug text-foreground transition-colors group-hover:text-emerald-700 sm:text-xl dark:group-hover:text-primary-active">
                {entry.title}
              </span>

              <span className="col-start-2 mt-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground sm:col-start-3 sm:mt-0 sm:text-right sm:text-[11px]">
                {entry.cat}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

function About() {
  const body = useReveal<HTMLDivElement>(0.12)

  return (
    <section id="about" className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="01 · About Us"
          title={
            <>
              Built on <span className={ACCENT}>13 Years</span> of Institutional
              Expertise
            </>
          }
        />

        <div
          ref={body.ref}
          className="mt-10 grid gap-8 sm:mt-12 lg:grid-cols-2 lg:gap-14"
          style={{
            opacity: body.shown ? 1 : 0,
            transform: body.shown ? 'none' : 'translateY(16px)',
            transition: `opacity 700ms ${EASE}, transform 700ms ${EASE}`,
          }}
        >
          <Figure
            src={IMAGES.about}
            alt="Campus"
            label="Institutional Excellence"
            className="h-56 sm:h-72 lg:h-full lg:min-h-[320px]"
          />

          <div className="flex flex-col gap-4 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
            <p>
              Founded in 2023, Euclideum Solutions Pvt Ltd formalizes years of
              experience within the education industry, shaped by real-world
              challenges across academic and administrative environments.
            </p>
            <p>
              This expertise originates from Campus Credential LLP, where our
              team worked closely with institutions for over a decade,
              supporting a large and diverse network of partner institutions
              across the country.
            </p>
            <p>
              Euclideum was formed to translate that understanding into robust,
              sustainable software systems that support mission-critical
              operations and institutional clarity.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 sm:gap-5">
          {[
            {
              title: 'Mission',
              text: 'To empower educational institutions through innovative digital solutions that simplify administration, enhance learning experiences, and improve operational efficiency while upholding the highest standards of integrity and professionalism.',
            },
            {
              title: 'Vision',
              text: 'To be a trusted technology foundation for institutions seeking operational clarity and future-ready resilience, enabling them to focus on outcomes and growth rather than operational complexity.',
            },
          ].map((card, i) => (
            <div
              key={card.title}
              className={cn(PANEL, 'rounded-lg p-6 sm:p-8')}
              style={{
                opacity: body.shown ? 1 : 0,
                transform: body.shown ? 'none' : 'translateY(14px)',
                transition: `opacity 600ms ${EASE} ${180 + i * 140}ms, transform 600ms ${EASE} ${180 + i * 140}ms`,
              }}
            >
              <h3 className="font-heading text-xl font-normal text-foreground">
                {card.title}
              </h3>
              <span
                aria-hidden
                className="mt-3 block h-px w-6 bg-emerald-600/70 dark:bg-primary-active/60"
              />
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                {card.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Overview() {
  const rail = useReveal<HTMLDivElement>(0.15)

  return (
    <section id="overview" className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-end lg:gap-14">
          <div>
            <SectionHead
              eyebrow="02 · Platform Overview"
              title={
                <>
                  IntelQI <span className={ACCENT}>UDC</span>
                </>
              }
              deck="IntelQI UDC serves as the central operational backbone for institutions. It unifies data, workflows, and decision-making into a single coherent foundation, eliminating fragmentation, redundancy, and operational blind spots across every department."
            />
          </div>
          <Figure
            src={IMAGES.team}
            alt="Team"
            label="Enterprise Collaboration"
            className="h-44 sm:h-52 lg:h-56"
          />
        </div>

        {/* The five modules as one hairline rail — the same gap-px grammar as
            the UDC page's module rail. */}
        <div
          ref={rail.ref}
          className={cn(
            'mt-10 border sm:mt-12',
            HAIRLINE,
            'rounded-lg overflow-hidden'
          )}
        >
          <div className="grid grid-cols-1 gap-px bg-border/70 sm:grid-cols-2 lg:grid-cols-5">
            {MODULES.map((m, i) => (
              <a
                key={m.num}
                href={`#module-${m.num}`}
                className="group bg-background p-5 transition-colors hover:bg-muted sm:p-6"
                style={{
                  opacity: rail.shown ? 1 : 0,
                  transform: rail.shown ? 'none' : 'translateY(10px)',
                  transition: `opacity 500ms ${EASE} ${i * 80}ms, transform 500ms ${EASE} ${i * 80}ms, background-color 150ms`,
                }}
              >
                <span
                  className={cn(
                    'text-[10px] font-semibold uppercase tracking-[0.2em]',
                    ACCENT
                  )}
                >
                  {m.num}
                </span>
                <span className="mt-3 block font-heading text-base font-normal leading-snug text-foreground">
                  {m.name}
                </span>
                <span className="mt-2 block text-xs text-muted-foreground">
                  {m.cat}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function ModuleBlock({ def }: { def: ModuleDef }) {
  const spec = useReveal<HTMLDivElement>(0.08)

  return (
    <section id={def.id} className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <div
          className={cn('grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-14')}
        >
          <div className={cn(def.flip && 'lg:order-2')}>
            <SectionHead
              eyebrow={`${def.num} · ${def.label}`}
              title={
                <>
                  {def.title} <span className={ACCENT}>{def.titleAccent}</span>
                </>
              }
              deck={def.desc}
            />
          </div>
          <Figure
            src={def.img}
            alt={def.label}
            label={def.imgLabel}
            className={cn('h-48 sm:h-60 lg:h-64', def.flip && 'lg:order-1')}
          />
        </div>

        {/* Specification sheet */}
        <div
          ref={spec.ref}
          className={cn(
            'mt-10 grid gap-x-12 border-t pt-2 sm:mt-12 md:grid-cols-2',
            HAIRLINE,
            def.columns.length > 2 && 'lg:grid-cols-3'
          )}
          style={{
            opacity: spec.shown ? 1 : 0,
            transform: spec.shown ? 'none' : 'translateY(14px)',
            transition: `opacity 700ms ${EASE} 80ms, transform 700ms ${EASE} 80ms`,
          }}
        >
          {def.columns.map((col) => (
            <div key={col.header}>
              <p
                className={cn(
                  'border-b pb-3 pt-4 text-[11px] font-semibold uppercase tracking-[0.2em]',
                  HAIRLINE,
                  ACCENT
                )}
              >
                {col.header}
              </p>
              {col.groups.map((group) => (
                <SpecGroup key={group.label} group={group} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Erp() {
  const grid = useReveal<HTMLDivElement>(0.12)

  return (
    <section id="erp" className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="06 · ERP Lifecycle"
          title={
            <>
              Access <span className={ACCENT}>Hierarchy</span>
            </>
          }
          deck="The ERP platform operates across three institutional life cycles. Access to each module is governed through a unified roles and rights framework, ensuring every stakeholder receives appropriate permissions."
        />

        <div ref={grid.ref} className="mt-10 sm:mt-12">
          <Eyebrow>ERP Life Cycles</Eyebrow>

          <div
            className={cn('mt-4 overflow-hidden rounded-t-lg border', HAIRLINE)}
          >
            <div className="grid grid-cols-1 gap-px bg-border/70 md:grid-cols-3">
              {LIFECYCLES.map((n, i) => (
                <div
                  key={n.label}
                  className="bg-background p-6"
                  style={{
                    opacity: grid.shown ? 1 : 0,
                    transform: grid.shown ? 'none' : 'translateY(10px)',
                    transition: `opacity 500ms ${EASE} ${i * 90}ms, transform 500ms ${EASE} ${i * 90}ms`,
                  }}
                >
                  <span
                    className={cn(
                      'text-[10px] font-semibold uppercase tracking-[0.2em]',
                      ACCENT
                    )}
                  >
                    0{i + 1}
                  </span>
                  <span className="mt-3 block font-heading text-lg font-normal text-foreground">
                    {n.label}
                  </span>
                  <span className="mt-1.5 block text-sm text-muted-foreground">
                    {n.sub}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Governance band — the one tinted surface in the section, the
              layer every lifecycle drains through. */}
          <div
            className={cn(
              'border-x border-b',
              HAIRLINE,
              'flex flex-col gap-1 bg-emerald-600/[0.06] px-6 py-5 dark:bg-primary-active/[0.06]',
              'border-l-[3px] border-l-emerald-600 dark:border-l-primary-active'
            )}
            style={{
              opacity: grid.shown ? 1 : 0,
              transition: `opacity 600ms ${EASE} 280ms`,
            }}
          >
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Access Governance Framework
            </span>
            <span className={cn('font-heading text-lg font-normal', ACCENT)}>
              Roles &amp; Rights Based Access Control
            </span>
            <span className="text-sm text-muted-foreground">
              Every ERP module across the three life cycles is governed through
              centralized roles and permissions.
            </span>
          </div>

          <div className="mt-8">
            <Eyebrow>Stakeholder Access Levels</Eyebrow>
          </div>

          <div
            className={cn('mt-4 overflow-hidden rounded-lg border', HAIRLINE)}
          >
            <div className="grid grid-cols-1 gap-px bg-border/70 sm:grid-cols-2 lg:grid-cols-5">
              {ROLE_CARDS.map((r, i) => (
                <div
                  key={r.title}
                  className="bg-background p-5 sm:p-6"
                  style={{
                    opacity: grid.shown ? 1 : 0,
                    transform: grid.shown ? 'none' : 'translateY(12px)',
                    transition: `opacity 500ms ${EASE} ${240 + i * 70}ms, transform 500ms ${EASE} ${240 + i * 70}ms`,
                  }}
                >
                  <span
                    aria-hidden
                    className="block h-px w-6 bg-emerald-600/70 dark:bg-primary-active/60"
                  />
                  <span className="mt-4 block font-heading text-base font-normal leading-snug text-foreground">
                    {r.title}
                  </span>
                  <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                    {r.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Implementation() {
  const steps = useReveal<HTMLDivElement>(0.1)

  return (
    <section
      id="implementation"
      className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28"
    >
      <div className="container mx-auto">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHead
              eyebrow="07 · Implementation"
              title={
                <>
                  A Proven <span className={ACCENT}>5-Step</span> Process
                </>
              }
              deck="Strategize, implement, and achieve institutional objectives with IntelQI's structured onboarding methodology built from real deployment experience across diverse institutional environments."
            />

            <div ref={steps.ref} className="mt-10 sm:mt-12">
              {IMPLEMENTATION_STEPS.map((s, i) => (
                <div
                  key={s.num}
                  className="flex gap-5"
                  style={{
                    opacity: steps.shown ? 1 : 0,
                    transform: steps.shown ? 'none' : 'translateY(12px)',
                    transition: `opacity 600ms ${EASE} ${i * 110}ms, transform 600ms ${EASE} ${i * 110}ms`,
                  }}
                >
                  <div className="flex flex-col items-center">
                    <span
                      className={cn(
                        'flex size-10 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tabular-nums',
                        'border-emerald-600/50 text-emerald-700 dark:border-primary-active/40 dark:text-primary-active'
                      )}
                    >
                      {s.num}
                    </span>
                    {i < IMPLEMENTATION_STEPS.length - 1 && (
                      <span
                        aria-hidden
                        className="my-1.5 w-px flex-1 bg-gradient-to-b from-emerald-600/30 to-transparent dark:from-primary-active/25"
                      />
                    )}
                  </div>
                  <div className="pb-9 pt-1.5">
                    <p className="font-heading text-lg font-normal text-foreground">
                      {s.title}
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                      {s.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:sticky lg:top-[calc(var(--header-h)+2rem)] lg:self-start">
            <Figure
              src={IMAGES.implementation}
              alt="Implementation"
              label="Proven Methodology"
              className="h-56 sm:h-72 lg:h-[420px]"
            />
            <div
              className={cn(
                PANEL,
                'mt-4 flex items-baseline gap-3 rounded-lg px-6 py-5'
              )}
            >
              <span className={cn('font-heading text-3xl font-normal', ACCENT)}>
                <AnimatedNumber value="5" />
              </span>
              <span className="text-sm text-muted-foreground">Phases</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function DownloadCta() {
  return (
    <section
      id="download"
      className="relative mt-16 scroll-mt-24 overflow-hidden sm:mt-20 md:mt-28"
    >
      {/* The dossier closes on the same field it opened on, fading upward. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-full"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 76% at 50% 40%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
          maskImage:
            'radial-gradient(ellipse 110% 76% at 50% 40%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
        }}
      />

      <div className={cn('relative z-10 border-t', HAIRLINE)}>
        <div className="container mx-auto py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <Eyebrow>Get the Brochure</Eyebrow>

            <h2 className="font-heading mt-6 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl">
              <ChromaText>
                Download the Complete{' '}
                <span className={ACCENT}>IntelQI UDC</span> Brochure
              </ChromaText>
            </h2>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Get the full product brochure in PDF. Share it with your team,
              present to stakeholders, or review offline at your convenience.
            </p>

            <div className="mt-9">
              <DownloadButton large />
            </div>
          </div>
        </div>

        {/* Closing folio line, mirroring the cover's. */}
        <div className={cn('border-t', HAIRLINE)}>
          <div className="container mx-auto flex flex-wrap items-center justify-between gap-2 py-3.5">
            <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Euclideum Solutions Private Limited
            </span>
            <a
              href="https://www.intelqi.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] font-medium tracking-[0.1em] text-muted-foreground transition-colors hover:text-foreground"
            >
              www.intelqi.com
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ProductBrochurePage() {
  return (
    <main className="bg-background pb-0">
      <Hero />
      <Contents />
      <About />
      <Overview />
      {MODULE_SECTIONS.map((def) => (
        <ModuleBlock key={def.id} def={def} />
      ))}
      <Erp />
      <Implementation />
      <DownloadCta />
    </main>
  )
}
